/**
 * ★ THE CATEGORY NAMES FOR THE CALENDAR'S DAY PANE - the owner's live walk of real cabinets
 * (2026-10-01) read "PERSONALJOURNEY · 2" under "How busy this day is".
 *
 * `AuthorCalendar.dayLoad[].byCategory` carries category CODES only, and the contract says why a
 * client must not make a name out of one: the catalogue is admin-managed and "its codes are not all
 * lower-case, so a client must not pattern-match them" (authors-api.yaml, `dayLoad`). So nothing
 * here splits camel case or guesses. The names come from the catalogue itself -
 * `listEditorCategories`, the same read the editor's Setup pane and the profile's subjects use,
 * `{ code, label }` per row - and a code the catalogue does not hold (or a catalogue that did not
 * load) is still printed verbatim, as it always was.
 *
 * The editor seam is built here the way the editor screen builds it (`createEditorApi()`, once per
 * mount): it answers from fixtures in the default build and over HTTP in a live one. The names come
 * in the cabinet's language, and the read is keyed on it, so a change of language asks again.
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from '../../i18n';
import type { CategoryList } from '../cabinet/api/editor-api';
import { createEditorApi } from '../cabinet/api/editor-api-source';
import { useAsync } from '../cabinet/state';

/** The catalogue as code -> name. Empty while it loads or when it did not load. */
export function useCategoryLabels(): ReadonlyMap<string, string> {
  const { i18n } = useTranslation('calendar');
  const editor = useMemo(() => createEditorApi(), []);
  const read = useCallback(
    (signal: AbortSignal): Promise<CategoryList> => editor.listEditorCategories(signal),
    [editor],
  );
  const catalogue = useAsync(read, `calendar:category-labels:${i18n.language}`);
  return useMemo(
    () => new Map((catalogue.data?.items ?? []).map((row) => [row.code, row.label])),
    [catalogue.data],
  );
}

/** A day-load category as the reader sees it: its catalogue name, else its code verbatim. */
export function categoryName(labels: ReadonlyMap<string, string>, code: string): string {
  return labels.get(code) ?? code;
}
